// AuthContext.tsx
import React, { createContext, useState, FC, ReactNode, useEffect, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { WatchedVideoData } from '../components/Quiz/QuizTypes';
import { getAuth, onAuthStateChanged, getIdToken } from "firebase/auth";
import { User } from '../hooks/user/UserTypes';
import { API_URL } from '@env';
import axios from 'axios';
import TriBoolean from '../hooks/utilities/ThreeBoolean';
import { useDebugLogger } from '../hooks/utilities/useDebugLogger';

type firebaseUser = {
  uid: string;
  email: string;
  displayName: string;
  // Add any other user profile fields you expect
};

type AuthContextType = {
  isAuthenticated: TriBoolean;
  setIsAuthenticated: (value: TriBoolean) => void;
  userProfile: User | null;
  setUserProfile: (profile: User) => void;
  updateUserFollowing: (userIds: string[]) => void;
  updateUserFollowingCount: (count: number) => void;
  updateWatchedVideos: (videoDatum: WatchedVideoData[]) => void;
  updateBlockedUsers: (userIds: string[]) => void;
  getToken: () => Promise<string | null>;
  getUserProfile: () => Promise<User>;
  authInitialized: boolean;
  // Added stable ref values that won't change with re-renders
  isAuthenticatedStable: () => TriBoolean;
  authInitializedStable: () => boolean;
  getUserProfileStable: () => User | null; // Added stable user profile accessor
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: TriBoolean.UNDETERMINED,
  setIsAuthenticated: () => {},
  userProfile: null,
  setUserProfile: () => {},
  updateUserFollowing: () => {},
  updateUserFollowingCount: () => {},
  updateWatchedVideos: () => {},
  updateBlockedUsers: () => {},
  getToken: async () => null,
  getUserProfile: async () => ({} as User),
  authInitialized: false,
  // Added default values for stable ref accessors
  isAuthenticatedStable: () => TriBoolean.UNDETERMINED,
  authInitializedStable: () => false,
  getUserProfileStable: () => null,
});

type AuthProviderProps = {
  children: ReactNode;
};

/**
 * Auth Provider Component - Manages authentication state for the application
 */
export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {

  const debug = useDebugLogger();

  debug.log("[AuthContext] AuthProvider initialized");

  // State for React rendering
  const [isAuthenticated, setIsAuthenticatedState] = useState<TriBoolean>(TriBoolean.UNDETERMINED);
  const [userProfile, setUserProfileState] = useState<User | null>(null);
  const [authInitialized, setAuthInitializedState] = useState<boolean>(false);
  
  // Refs for stable values across re-renders
  const isAuthenticatedRef = useRef<TriBoolean>(TriBoolean.UNDETERMINED);
  const userProfileRef = useRef<User | null>(null);
  const authInitializedRef = useRef<boolean>(false);
  const authCheckRunningRef = useRef<boolean>(false);
  
  // Track if authentication check has completed
  const authCheckCompletedRef = useRef<boolean>(false);
  
  // Component mount tracking
  const mountCountRef = useRef<number>(0);
  const componentIdRef = useRef<string>(`auth-${Date.now()}`);

  useEffect(() => {
    mountCountRef.current += 1;
    debug.log(`[AuthContext ${componentIdRef.current}] Mount count: ${mountCountRef.current}`);
    
    return () => {
      debug.log(`[AuthContext ${componentIdRef.current}] Component unmounting`);
    };
  }, []);

  // Synchronized setters that update both state and refs
  const setIsAuthenticated = (value: TriBoolean) => {
    debug.log(`[AuthContext] Setting isAuthenticated to: ${value}`);
    isAuthenticatedRef.current = value;
    setIsAuthenticatedState(value);
  };

  const setUserProfile = (profile: User | null) => {
    userProfileRef.current = profile;
    setUserProfileState(profile);
  };

  const setAuthInitialized = (value: boolean) => {
    debug.log(`[AuthContext] Setting authInitialized to: ${value}`);
    authInitializedRef.current = value;
    setAuthInitializedState(value);
  };

  // Stable accessors that can be used regardless of re-renders
  const isAuthenticatedStable = () => {
    debug.log(`[AuthContext] isAuthenticatedStable called, value: ${isAuthenticatedRef.current}, type: ${typeof isAuthenticatedRef.current}`);
    return isAuthenticatedRef.current;
  };
  
  const authInitializedStable = () => {
    debug.log(`[AuthContext] authInitializedStable called, value: ${authInitializedRef.current}`);
    return authInitializedRef.current;
  };
  
  // Add a way to access the stable userProfile
  const getUserProfileStable = () => {
    debug.log(`[AuthContext] getUserProfileStable called, profile: ${userProfileRef.current?.username || 'null'}`);
    return userProfileRef.current;
  };

  // Function to update followingUserIds in the userProfile
  const updateUserFollowing = (userIds: string[]) => {
    if (userProfileRef.current) {
      const updatedProfile = { ...userProfileRef.current, followingUserIds: userIds };
      setUserProfile(updatedProfile);
    }
  };

  const updateUserFollowingCount = (count: number) => {
    if (userProfileRef.current) {
      const updatedProfile = { ...userProfileRef.current, followingCount: count };
      setUserProfile(updatedProfile);
    }
  };

  // Effect to handle authentication check
  useEffect(() => {
    // If auth check has already run, set state from refs and exit
    if (authCheckCompletedRef.current) {
      debug.log("[AuthContext] Auth check already completed, restoring state from refs");
      setAuthInitializedState(authInitializedRef.current);
      setIsAuthenticatedState(isAuthenticatedRef.current);
      setUserProfileState(userProfileRef.current);
      return;
    }

    // Don't run multiple auth checks simultaneously
    if (authCheckRunningRef.current) {
      debug.log("[AuthContext] Auth check already running, skipping");
      return;
    }

    async function checkSession() {
      // Mark as running to prevent duplicate checks
      authCheckRunningRef.current = true;
      
      try {
        debug.log("[AuthContext] Checking session...");
        const userProfileString = await AsyncStorage.getItem('userProfile');

        if (userProfileString) {
          const savedProfile: User = JSON.parse(userProfileString);
          debug.log("[AuthContext] Found user profile in AsyncStorage:", savedProfile.username);

          try {
            const response = await axios.get(`${API_URL}/users/auth/${savedProfile?.username}`, 
              { withCredentials: true }
            );

            if (response.data && response.data.user) {
              debug.log("[AuthContext] Session valid, setting auth to TRUE");
              const userProfileResponse = response.data.user;

              const userData: User = {
                username: userProfileResponse.username,
                bio: userProfileResponse.bio,
                watchedVideos: userProfileResponse.watchedVideos,
                profilePicture: userProfileResponse.profilePicture,
                blockedUserIds: userProfileResponse.blockedUserIds,    
                correctSoundUrl: userProfileResponse.correctSound,
                incorrectSoundUrl: userProfileResponse.incorrectSound,
                followersCount: userProfileResponse.followersCount,
                followingCount: userProfileResponse.followingCount,
                followersUserIds: userProfileResponse.followersUserIds,
                followingUserIds: userProfileResponse.followingUserIds,
                uploadedVideos: userProfileResponse.uploadedVideos,
              };
              await AsyncStorage.setItem('userProfile', JSON.stringify(userData));
              
              // Update both state and refs
              setUserProfile(userData);
              setIsAuthenticated(TriBoolean.TRUE);
            } else {
              debug.log("[AuthContext] No valid user in response, setting auth to FALSE");
              setIsAuthenticated(TriBoolean.FALSE);
            }
          } catch (error) {
            debug.log("[AuthContext] API call failed, setting auth to FALSE:", error);
            setIsAuthenticated(TriBoolean.FALSE);
          }
        } else {
          debug.log("[AuthContext] No user profile in AsyncStorage, setting auth to FALSE");
          setIsAuthenticated(TriBoolean.FALSE);
        }
      } catch (error) {
        // 401 or other error means not logged in
        debug.log("[AuthContext] Error checking session, setting auth to FALSE:", error);
        setIsAuthenticated(TriBoolean.FALSE);
      } finally {
        // Mark auth as initialized and auth check as completed
        debug.log("[AuthContext] Auth check completed, setting authInitialized to TRUE");
        setAuthInitialized(true);
        authCheckCompletedRef.current = true;
        authCheckRunningRef.current = false;
      }
    }
    
    // Run check session
    checkSession();
  }, []);

  const getToken = () => {
    return new Promise<string | null>((resolve, reject) => {
      const auth = getAuth(); // Get the Firebase Auth instance
      // Setting up a one-time listener to auth state change
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe(); // Unsubscribe after getting the user state
  
        if (!user) {
          resolve(null); // Resolve the promise with null if no user is signed in
          return;
        }
        
        const newIdToken = await getIdToken(user, true); // Force token refresh
        resolve(newIdToken); // Resolve the promise with the new token
      }, reject); // In case of an error, reject the promise
    });
  };

  const updateWatchedVideos = (videoDatum: WatchedVideoData[]) => {
    if (userProfileRef.current) {
      const updatedProfile = { ...userProfileRef.current, watchedVideos: videoDatum };
      setUserProfile(updatedProfile);
    }
  };

  const updateBlockedUsers = (userIds: string[]) => {
    if (userProfileRef.current) {
      const updatedProfile = { ...userProfileRef.current, blockedUserIds: userIds };
      setUserProfile(updatedProfile);
    }
  };  

  const getUserProfile = async () => {
    try {
      if (userProfileRef.current && userProfileRef.current.username) {
        return userProfileRef.current;
      } else {
        const userProfileAsyncStorage = await AsyncStorage.getItem('userProfile');
        if (userProfileAsyncStorage) {
          const userProfileFromAsync: User = JSON.parse(userProfileAsyncStorage);
          // Update ref and state
          setUserProfile(userProfileFromAsync);
          return userProfileFromAsync;
        }
      }
      return {} as User;
    } catch (error) {
      debug.error("[AuthContext] Error fetching userProfile from AsyncStorage:", error);
      return {} as User;
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      setIsAuthenticated,
      userProfile,
      setUserProfile,
      updateUserFollowing,
      updateUserFollowingCount,
      updateWatchedVideos,
      updateBlockedUsers,
      getToken,
      getUserProfile,
      authInitialized,
      // Expose stable accessors
      isAuthenticatedStable,
      authInitializedStable,
      getUserProfileStable,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;